<script>
   /*************************************************************/
   /* ModelCalculator                                           */
   /* -------------------------                                 */
   /* component for training a PCA model                        */
   /*************************************************************/

   import { pca_fit } from '../wasm/dist/pca';
   import { app_pca_predict, genhash } from '../utils.js';

   import { Switch, Container, Button, Spinner, ButtonCancel, Range } from 'svelte-controls-basic';

   let {
      WASM,                      // promise with WASM methods
      dataset,                   // object with training set
      outliers = [],             // vector with dataset row numbers (from 0) marked as outliers
      resobj = $bindable(),      // object with PCA results
      model = $bindable(),       // object with PCA model
      downloadModel = null,
      downloadResults = null
   } = $props();

   // error handling parameetrs
   let status = $state('neutral');
   let errMsg = $state('');

   // model calculation parameters
   let ncomp = $state(1);
   let center = $state(true);
   let scale = $state(false);


   // promise for model calculation method
   let promise = $state();

   /**
    * Fits PCA model and apply it to training set.
    * @param {Object} dataset - object with training set.
    * @param {Number} ncomp - number of PCs in the model.
    * @param {boolean} center - mean center data columns or not.
    * @param {boolean} scale - standardize data columns or not.
    *
    * Method does not return anything but assign values to the global parameters "model" and "res".
    */
   async function computeModel(dataset, ncomp, center, scale) {
      promise = WASM()
         .then(Module => {
            status = 'computing';

            // fit model and add extra parameters
            const m = pca_fit(Module, dataset.values, ncomp, center, scale, outliers);
            m['hash'] = genhash();                  // unique hash number to trace model updates

            m['exclvars'] = [];                            // array with indices of excluded variables - reserved for future use
            m['varlabels'] = dataset.varlabels;            // array with labels for variables
            m['varindices'] = dataset.varindices;          // variable indices
            m['varvalues'] = dataset.varvalues;            // variable values if available (wavelength, etc.)
            m['varvaluesName'] = dataset.varvaluesName;    // title for variable axis
            m['varvaluesUnits'] = dataset.varvaluesUnits;  // units if variales are represented by numeric values, e.g. nm
            m['varrev'] = dataset.varrev                   // if variables must be reversed
            m['prep'] = [];                                // array with preprocessing methods — reserved for future use

            // make predictions
            const r = app_pca_predict(Module, m, dataset, outliers);

            // set global variables and status of the component
            model = m;
            resobj = r;
            status = 'success';
         })
         .catch(e =>{
            // something went wrong
            console.error(e);
            model = null;
            status = 'error';
            errMsg = e;
         });
   }

   /**
    * Event handler for computing the model
    * @param e — event object.
    */
   function compute(e) {
      status = 'neutral';
      errMsg = null;
      computeModel(dataset, ncomp, center, scale);
   }

   /**
    * Resets the component status.
    */
   function reset() {
      status = 'neutral';
      promise = null;
      resobj = null;
      model = null;
      errMsg = null;
   }

   // set initial number of components depending on dataset size and manual limit of A = 20
   let maxNComp = $derived(dataset ? Math.min(dataset.values.nrows - 2 - outliers.length, dataset.values.ncols - 1, 50) : null);


   // if data file is empty reset status and error messages
   $effect(() => {
      if (dataset === null) reset();
   })

   $effect(() => {
      ncomp = maxNComp ? Math.min(maxNComp, 20) : null;
   });
</script>

{#if (dataset && !model) && status !== 'computing'}
   <!-- settings for global model calculation -->
   <Container name="ncomp" label="# of PCs">
      <Range id="ncomp" bind:value={ncomp} min={1} max={maxNComp} step={1} decNum={0} />
   </Container>
   <Container name="center" label="Center">
      <Switch id="center" bind:value={center} options={["no", "yes"]} />
   </Container>
   <Container name="scale" label="Scale">
      <Switch id="scale" bind:value={scale} options={["no", "yes"]} />
   </Container>
   <Container name="create-model" label=" ">
      <Button onclick={compute} text="Create model" />
   </Container>
{/if}


{#if promise}

   <!-- calculation is in process -->
   {#await promise}
      <Container name="spinner">
         <Spinner />
         <p>Computing global model...</p>
      </Container>

   {:catch error}
      <!-- error message if failed -->
      <Container name="error" status="error">
         <p>{error}</p>
      </Container>
   {/await}

{/if}

{#if model }
<Container name="info">
   <p><span style="color:#a0a0a0;">PCA model:</span> {model.ncomp} PCs{model.center ? ', centered' : ''}{model.scale ? ', scaled' : ''}</p>
   <ButtonCancel onclick={reset} />
</Container>
{#if downloadModel && downloadResults}
<Container name="download-buttons" label='Download'>
   <div class="buttons">
   <Button onclick={() => downloadResults(resobj, dataset, "train", outliers, model)} text="Results" />
   <Button onclick={() => downloadModel(model)} text="Model" />
   </div>
</Container>
{/if}
{/if}


<!-- error message -->
{#if status === 'error' || errMsg}
<Container name="error" status="error">
   <p>{errMsg}</p>
</Container>
{/if}

